import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Music, Play, Clock, Loader2, X, Youtube } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MeditationsPage = () => {
  const [meditations, setMeditations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchMeditations();
  }, []);

  const fetchMeditations = async () => {
    try {
      const response = await axios.get(`${API_URL}/meditations`, {
        headers: getAuthHeader()
      });
      setMeditations(response.data);
    } catch (error) {
      console.error('Failed to fetch meditations:', error);
      toast.error('Failed to load meditations');
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (meditation) => {
    if (meditation.source === 'youtube' && meditation.video_id) {
      setSelectedVideo(meditation);
    } else if (meditation.audio_url) {
      window.open(meditation.audio_url, '_blank');
      toast.success(`Starting: ${meditation.title}`);
    }
  };

  const categoryColors = {
    morning: 'bg-amber-100 text-amber-700',
    stress: 'bg-blue-100 text-blue-700',
    evening: 'bg-indigo-100 text-indigo-700',
    affirmations: 'bg-pink-100 text-pink-700',
    relaxation: 'bg-green-100 text-green-700',
    meditation: 'bg-purple-100 text-purple-700',
    general: 'bg-stone-100 text-stone-700'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meditations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 mb-2">Meditations</h1>
          <p className="text-stone-600">Find your peace with guided practices</p>
        </div>
        {meditations.some(m => m.source === 'youtube') && (
          <div className="flex items-center gap-2 text-sm text-stone-500 bg-stone-100 px-3 py-1.5 rounded-full">
            <Youtube className="w-4 h-4 text-red-500" />
            Auto-synced from YouTube
          </div>
        )}
      </div>

      {/* Meditations Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {meditations.map((meditation) => (
          <div 
            key={meditation.id} 
            className="card-cosmic overflow-hidden card-hover group cursor-pointer"
            onClick={() => handlePlay(meditation)}
            data-testid={`meditation-card-${meditation.id}`}
          >
            <div className="aspect-video relative overflow-hidden">
              <img 
                src={meditation.thumbnail_url}
                alt={meditation.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-xl">
                  <Play className="w-8 h-8 text-primary ml-1" />
                </div>
              </div>

              {/* Duration badge */}
              <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur text-white px-2 py-1 rounded-lg flex items-center gap-1 text-sm">
                <Clock className="w-3 h-3" />
                {meditation.duration_minutes} min
              </div>

              {/* YouTube badge */}
              {meditation.source === 'youtube' && (
                <div className="absolute top-3 right-3">
                  <Youtube className="w-6 h-6 text-red-500 drop-shadow-lg" />
                </div>
              )}
            </div>
            
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${categoryColors[meditation.category] || categoryColors.general}`}>
                  {meditation.category}
                </span>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2 line-clamp-2">{meditation.title}</h3>
              <p className="text-sm text-stone-600 line-clamp-2">{meditation.description}</p>
            </div>
          </div>
        ))}
      </div>

      {meditations.length === 0 && (
        <div className="card-cosmic p-12 text-center">
          <Music className="w-12 h-12 text-stone-400 mx-auto mb-4" />
          <p className="text-stone-600">No meditations available yet. Check back soon!</p>
        </div>
      )}

      {/* Video Player Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="sm:max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="font-syne pr-8">{selectedVideo?.title}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full">
            {selectedVideo && (
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${selectedVideo.video_id}?autoplay=1&rel=0`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeditationsPage;
